import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { stakeholderEngagementApi } from '../../api/stakeholderEngagement'

/**
 * The one notification feed, read by every place that shows it: the work layout's newsfeed badge, the
 * newsfeed page and the admin console's Updates page. They share these query keys, so marking the
 * feed read anywhere clears the badge everywhere.
 */
const NOTIFICATIONS_KEY = ['stakeholderNotifications']
const UNREAD_COUNT_KEY = ['stakeholderUnreadNotifications']

/** How many notifications are unread, kept fresh for the sidebar badges. */
export function useUnreadNotificationCount(enabled: boolean): number {
  const { data } = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: () => stakeholderEngagementApi.unreadCount().then((res) => res.data),
    enabled,
    refetchInterval: 60_000,
  })
  return data?.count ?? 0
}

export function useStakeholderNotifications() {
  const queryClient = useQueryClient()

  const { data: notifications = [], isLoading, isError, refetch } = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => stakeholderEngagementApi.notifications().then((res) => res.data),
  })

  const markAllRead = useMutation({
    mutationFn: () => stakeholderEngagementApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY })
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    },
  })

  return {
    notifications,
    unreadCount: notifications.filter((notification) => !notification.readAt).length,
    isLoading,
    isError,
    refetch,
    markAllRead,
  }
}
